import appStore from '@store/appStore';
import { meLogOut } from '@store/reducers/meReducer';
import { meCheckIfLoggedIn } from '@store/reducers/reducerHelpers/meCheckIfLoggedIn';
import type { AxiosError, AxiosResponse } from 'axios';

let isCheckingAuth = false;

/**
 * Creates an authentication interceptor for axios that handles 401 responses
 * by verifying authentication status before logging out the user.
 */
export const createAuthInterceptor = () => ({
  onFulfilled: (response: AxiosResponse) => response,
  onRejected: async (error: AxiosError) => {
    // Only handle 401 responses
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // Skip if this is already a meCheckIfLoggedIn request to prevent infinite loop
    if (error.config?.url?.includes('/me/check')) {
      return Promise.reject(error);
    }

    // Prevent multiple simultaneous auth checks
    if (isCheckingAuth) {
      return Promise.reject(error);
    }

    isCheckingAuth = true;

    try {
      // Dispatch the auth check
      const resultAction = await appStore.dispatch(meCheckIfLoggedIn());

      // Check if the auth check was successful
      if (meCheckIfLoggedIn.fulfilled.match(resultAction)) {
        // Auth check succeeded, user is still logged in
        // The original 401 might be for a different reason (permissions, etc.)
        isCheckingAuth = false;
        return Promise.reject(error);
      }

      // Check if the auth check also returned 401
      if (meCheckIfLoggedIn.rejected.match(resultAction)) {
        const rejectedPayload = resultAction.payload as { error?: { statusCode?: number } };
        if (rejectedPayload?.error?.statusCode === 401) {
          // Auth check confirmed user is not authenticated
          // Log them out and redirect
          await appStore.dispatch(meLogOut());

          // Redirect to login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      }
    } catch (err) {
      console.error('Error during auth verification:', err);
    } finally {
      isCheckingAuth = false;
    }

    return Promise.reject(error);
  },
});
